<template>
  <div>
    <barcode-scanner
      v-if="scanReference !== null"
      @decode="scanDone($event, scanReference)"
      @close="scanClosed" />
    <div class="spl-edit-template__step spl-book-general">
      <div class="row">
        <div class="col4">
          <!-- *********************** Direction  *********************************** -->
          <form-label>Direction</form-label>
          <form-radiobutton-group
            :options="directionOptions"
            :horizontal="true"
            :toggle="true"
            class="spl-book-general__direction"
            :value="directionValue"
            @radiobuttonChanged="direction_set" />
        </div>
      </div>
      <!-- *********************** Return parcel  *********************************** -->
      <div v-if="bookingData.book_parcel_model.Is_Forward === false && bookingType === 'parcel'">
        <h4>Copy parcel data</h4>
        <div class="spl-book-general__return">
          <div class="spl-book-general__return__source-reference">
            <form-label>Original Reference</form-label>
            <form-input
              v-model="returnCounterNumber"
              data-cy="book-general-return-reference"
              :required="false"
              :enable-trim="true"
              placeholder="Reference Number" />
          </div>
          <div class="spl-book-general__return__scan">
            <form-label />
            <form-button
              @onClick="openScanner('originalReference')">
              scan
            </form-button>
          </div>
          <div class="spl-book-general__return__copy-from">
            <form-label />
            <form-button
              data-cy="book-general-copy-from"
              @onClick="copyFromReturn">
              create from
            </form-button>
          </div>
        </div>
        <p v-if="returnParcel">
          <span><strong>Parcel selected:</strong></span><br>
          <span :class="isDateBefore(returnParcel) ? 'spl-return-modal__parcel--red' : ''">{{ formatDate(returnParcel.Booked_Pickup_Date, true,'YYYY-MM-DD') }}</span>
          <span> — {{ returnParcel.References[0].Reference_Text }}</span><br>
          <span><i>Destination: </i>{{ returnParcel.Delivery_Address.Name_1 + ', ' + returnParcel.Delivery_Address.Street_1 + ', ' + returnParcel.Delivery_Address.Postal_Code + ' ' + returnParcel.Delivery_Address.City + ', ' + returnParcel.Delivery_Address.Country_Name }}</span>
          <span>{{ returnParcel.Contact_Name }}</span>
        </p>
      </div>
      <h4>Parcel Data</h4>
      <div class="row">
        <div class="col4">
          <!-- *********************** Customer  *********************************** -->
          <form-label>Customer</form-label>
          <form-filtered-select
            :required="true"
            :options="customerOptions"
            :selected-key="bookingData.book_parcel_model.Customer_ID"
            :invalid="$v.bookingData.book_parcel_model.Customer_ID.$error"
            class="spl-book-general__customer"
            @close="$v.bookingData.book_parcel_model.Customer_ID.$touch()"
            @updateDropdownValue="customer_set" />
          <span v-if="$v.bookingData.book_parcel_model.Customer_ID.$error" class="error">Customer is required</span>
        </div>
        <div class="col1" />
        <div class="col4">
          <!-- *********************** Country  *********************************** -->
          <form-label>Country</form-label>
          <form-filtered-select
            :required="true"
            :options="countryOptions"
            :selected-key="bookingData.book_parcel_model.Country_ID"
            :invalid="$v.bookingData.book_parcel_model.Country_ID.$error"
            class="spl-book-general__country"
            @close="$v.bookingData.book_parcel_model.Country_ID.$touch()"
            @updateDropdownValue="country_set" />
          <span v-if="$v.bookingData.book_parcel_model.Country_ID.$error" class="error">Country is required</span>
        </div>
      </div>
      <br>
      <div class="row">
        <div class="col4">
          <!-- *********************** Service Level  *********************************** -->
          <form-label>Service Level</form-label>
          <form-filtered-select
            :required="true"
            :options="serviceLevel"
            :selected-key="bookingData.book_parcel_model.Service_Level_ID"
            :invalid="$v.bookingData.book_parcel_model.Service_Level_ID.$error"
            class="spl-book-general__service-level"
            @close="$v.bookingData.book_parcel_model.Service_Level_ID.$touch()"
            @updateDropdownValue="service_level_set" />
          <span v-if="$v.bookingData.book_parcel_model.Service_Level_ID.$error" class="error">Service Level is required</span>
        </div>
        <div class="col1" />
        <div class="col4">
          <!-- *********************** Booked Pickup date  *********************************** -->
          <form-label>Booked Pickup Date</form-label>
          <form-datepicker
            v-model="bookingData.book_parcel_model.Booked_Pickup_Date"
            :invalid="$v.bookingData.book_parcel_model.Booked_Pickup_Date.$invalid"
            class="spl-book-general__booked-pickup-date" />
          <span v-if="!$v.bookingData.book_parcel_model.Booked_Pickup_Date.minValue" class="error">Pickup date can be maximum {{ constants.BOOKING_DATE_VALID_DAYS_IN_PAST }} days in the past.</span>
          <span v-if="!$v.bookingData.book_parcel_model.Booked_Pickup_Date.maxValue" class="error">Pickup date can be maximum {{ constants.BOOKING_DATE_VALID_DAYS_IN_FUTURE }} days in the future.</span>
        </div>
      </div>

      <!-- *********************** Counternumber  *********************************** -->
      <parcel-counter-number
        ref="counterNumber"
        :use-shipping-reference="bookingType === 'distributed'"
        :booking-data="bookingData"
        @scan="openScanner('counterNumber')"
        @numberChanged="onCounterNumberChanged" />

      <!-- Next step footer -->
      <general-footer
        :cancel-visible="$mq === 'sm'"
        :disable-positive="!isValid"
        :disable-negative="true"
        :positive-label="nextStepLabel"
        negative-label="Back"
        @positiveClicked="nextStep" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { integer, required, minValue } from 'vuelidate/lib/validators'
import Helpers from '@/helpers.vue'
import { merge, cloneDeep } from 'lodash'
import moment from 'moment'
import constants from '@/constants'

export default {
  name: 'BookingGeneralInformation',
  mixins: [Helpers],
  props: ['nextStepLabel', 'bookingData', 'bookingType', 'returnParcel'],
  data () {
    return {
      returnCounterNumber: null,
      serviceLevel: null,
      stepInformation: {
        label: 'General Information'
      },
      constants,
      scanReference: null
    }
  },
  validations: {
    bookingData: {
      book_parcel_model: {
        Customer_ID: {
          required,
          integer,
          minValue: minValue(0)
        },
        Country_ID: {
          required,
          integer,
          minValue: minValue(0)
        },
        Service_Level_ID: {
          required,
          integer,
          minValue: minValue(0)
        },
        Booked_Pickup_Date: {
          required,
          minValue: (value) => {
            return value > moment().subtract(constants.BOOKING_DATE_VALID_DAYS_IN_PAST, 'days').toISOString()
          },
          maxValue: (value) => {
            return value < moment().add(constants.BOOKING_DATE_VALID_DAYS_IN_FUTURE, 'days').toISOString()
          }
        }
      }
    }
  },
  created () {
    if (!this.serviceLevel) {
      this.serviceLevel = this.serviceLevelOptions
    }
  },
  computed: {
    ...mapGetters('country', ['countryOptions']),
    ...mapGetters('customer', ['customerOptions']),
    ...mapGetters('serviceLevel', ['serviceLevelOptions']),
    directionOptions () {
      var options = []
      if (this.bookingType === 'distributed') {
        if (this.userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.BOOK_CONSOLIDATED_FORWARD)) {
          options.push({ label: 'Forward', value: true })
        }
        if (this.userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.BOOK_CONSOLIDATED_RETURN)) {
          options.push({ label: 'Return', value: false })
        }
      } else {
        if (this.userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.BOOK_FORWARD)) {
          options.push({ label: 'Forward', value: true })
        }
        if (this.userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.BOOK_RETURN)) {
          options.push({ label: 'Return', value: false })
        }
      }
      return options
    },
    directionValue () {
      var direction = true
      if (this.bookingData && this.bookingData.book_parcel_model) {
        direction = this.bookingData.book_parcel_model.Is_Forward
      }
      if (this.bookingType === 'distributed') {
        if (this.userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.BOOK_CONSOLIDATED_FORWARD) && !this.userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.BOOK_CONSOLIDATED_RETURN)) {
          direction = true
          this.direction_set(true)
        } else if (this.userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.BOOK_CONSOLIDATED_RETURN) && !this.userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.BOOK_CONSOLIDATED_FORWARD)) {
          direction = false
          this.direction_set(false)
        }
      } else {
        if (this.userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.BOOK_FORWARD) && !this.userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.BOOK_RETURN)) {
          direction = true
          this.direction_set(true)
        } else if (this.userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.BOOK_RETURN) && !this.userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.BOOK_FORWARD)) {
          direction = false
          this.direction_set(false)
        }
      }
      return direction
    }
  },
  mounted () {
    this.$v.$reset()
    this.$watch(vm => [vm.bookingData.book_parcel_model.Country_ID, vm.bookingData.book_parcel_model.Customer_ID, vm.bookingData.book_parcel_model.Is_Forward], val => {
      if (this.bookingData.book_parcel_model.Country_ID !== -1 && this.bookingData.book_parcel_model.Customer_ID !== -1) {
        this.getProfiler(this.bookingData.book_parcel_model.Customer_ID, this.bookingData.book_parcel_model.Country_ID, this.bookingData.book_parcel_model.Is_Forward)
      }
    },
    {
      immediate: true,
      deep: true
    })
  },
  methods: {
    ...mapActions('contact', ['getContacts']),
    ...mapActions('profiler', ['loadProfiler']),
    reset () {
      this.$refs.counterNumber.reset()
    },
    typeReturnNumber () {
      this.counter_number_invalid = true
    },
    getProfiler (customer_id, country_id, is_forward) {
      var profileFilter = { Customer_ID: customer_id, Country_ID: country_id, Is_Forward: is_forward }
      this.loadProfiler(profileFilter).then(() => {
        this.profileData = JSON.parse(window.sessionStorage.getItem('Profiler_List'))
        if (this.profileData !== null && this.profileData.Fields.length > 0) {
          const found = this.profileData.Fields.find(el => el.Name === 'SERVICE LEVEL')
          if (found && found.Options.length > 0) {
            const filteredArray = []
            found.Options.forEach(element => {
              this.serviceLevelOptions.forEach(el => {
                if (element === el.value.toString()) {
                  filteredArray.push(el)
                }
              })
            })
            this.serviceLevel = filteredArray
          }
        } else {
          this.serviceLevel = this.serviceLevelOptions
        }
        this.$forceUpdate()
      }).catch((error) => {
        window.bus.$emit('openErrorModal', error.ExceptionMessage)
      })
    },
    // Copies a parcel from a selected parcel
    copyFromReturn () {
      this.$emit('copyFromReturn', this.returnCounterNumber)
    },
    isDateBefore (parcel) {
      return moment(parcel.Booked_Pickup_Date).add(2, 'months').isBefore()
    },

    // Set the direction
    direction_set (newValue) {
      this.bookingData.book_parcel_model.Is_Forward = newValue
    },

    // Set the customer ID from the dropdown choice
    customer_set (item) {
      if (!item) {
        this.bookingData.book_parcel_model.Customer_ID = -1
        this.$v.bookingData.book_parcel_model.Customer_ID.$touch()
        return
      }
      this.bookingData.book_parcel_model.Customer_ID = item.value
      // Load the contacts of we also have the country
      if (this.bookingData.book_parcel_model.Country_ID > -1) {
        this.updateContacts()
      }
      // Clear related related fields
      if (this.bookingData.book_parcel_model.Is_Forward === true) {
        // TODO COMMENTED OUT FOR DEMO PURPOSES !!! NEEDS TO  BE ADDED LATER AGAIN
        // this.bookingData.book_parcel_model.Pickup_Address_ID = -1
        // this.bookingData.book_parcel_model.Delivery_Address_ID = -1
        this.bookingData.pickup_address_object = {}
        this.bookingData.pickup_address_formatted = ''
        this.bookingData.delivery_address_object = {}
        this.bookingData.delivery_address_formatted = ''
      }
      // Validate the step
      this.$v.bookingData.book_parcel_model.Customer_ID.$touch()
    },

    // Set the country ID from the dropdown choice
    country_set (item) {
      if (!item) {
        this.bookingData.book_parcel_model.Country_ID = -1
        return
      }
      this.bookingData.book_parcel_model.Country_ID = item.value

      // Load the contacts of we also have the customer
      if (this.bookingData.book_parcel_model.Customer_ID > -1) {
        this.updateContacts()
      }
    },

    // Set the service level ID from the dropdown choice
    service_level_set (item) {
      if (!item) {
        this.bookingData.book_parcel_model.Service_Level_ID = -1
        this.$v.bookingData.book_parcel_model.Service_Level_ID.$touch()
        return
      }
      this.bookingData.book_parcel_model.Service_Level_ID = item.value
      this.$v.bookingData.book_parcel_model.Service_Level_ID.$touch()
    },

    // ************************ Get the contact list based on customer/country ************************
    updateContacts () {
      this.getContacts({
        customerID: this.bookingData.book_parcel_model.Customer_ID,
        countryID: this.bookingData.book_parcel_model.Country_ID
      })
    },

    isValid () {
      return !this.$v.$invalid && !this.$refs.counterNumber.isInvalid()
    },

    nextStep () {
      this.$v.bookingData.book_parcel_model.Customer_ID.$touch()
      this.$v.bookingData.book_parcel_model.Country_ID.$touch()
      this.$v.bookingData.book_parcel_model.Service_Level_ID.$touch()
      this.$refs.counterNumber.touch()

      if (!this.$v.$invalid && !this.$refs.counterNumber.isInvalid()) {
        this.$emit('nextStep', this.bookingData)
      }
    },

    // ************************ CounterNumber Special Function ************************
    onCounterNumberChanged (bookingData) {
      merge(this.bookingData, bookingData)
    },
    openScanner (reference) {
      this.scanReference = reference
    },
    scanDone (result, reference) {
      if (result.indexOf('JSIM') !== -1) {
        result = result.substr(1)
      } else if (result.indexOf('D|') !== -1) {
        result = result.substr(2)
      }
      if (reference === 'originalReference') {
        this.returnCounterNumber = result
        this.copyFromReturn()
      } else if (reference === 'counterNumber') {
        this.$refs.counterNumber.setCounterNumber(result)
      }
      this.scanReference = null
    },
    scanClosed () {
      this.scanReference = null
    }
  }
}
</script>

<style lang="scss">

@import '../../variables.scss';
.spl-book-general {
  &__return {
    display: flex;
    flex-flow: row wrap;

     &__source-reference {
       max-width: 250px;
       margin-right: 20px;
      }
      &__scan button {
        min-width: 110px;
      }
   }
}
@include mobile () {
  .spl-book-general {
    & .spl-button {
      height: 40px;
      font-size: 14px;
    }
    &__return {
      display: flex;
      &__source-reference, &__source-reference input {
        width: 100%;
        max-width: none;
      }
    }
  }
}
</style>
